import { z } from 'zod';
import { CrossingEvent } from './crossing.ts';
import { RoundManifest } from './round.ts';
import { GameRoundId, Sha256Hex, UtcInstant } from './primitives.ts';

export const EvidenceStatus = z.enum(['COMPLETE', 'INSUFFICIENT', 'SIMULATION_ONLY']);

/** Receipt from the external witness ("this hash existed at receivedAt"). */
export const WitnessReceipt = z.object({
  witness: z.string(),
  seq: z.number().int().nonnegative(),
  subjectKind: z.enum(['MANIFEST', 'RESULT']),
  subjectRef: z.string(),
  subjectHash: Sha256Hex,
  receivedAt: UtcInstant,
  prevReceiptHash: Sha256Hex,
  receiptHash: Sha256Hex,
  keyId: z.string(),
  signature: z.string(),
});
export type WitnessReceipt = z.infer<typeof WitnessReceipt>;

export const Signature = z.object({ keyId: z.string(), algorithm: z.literal('Ed25519'), signature: z.string() });

/** One media segment of the counting window: raw and privacy-redacted renditions are hashed separately. */
export const EvidenceSegment = z.object({
  sourceEpoch: z.string(),
  seq: z.number().int().nonnegative(),
  ptsStartMicros: z.number().int().nonnegative(),
  ptsEndMicros: z.number().int().nonnegative(),
  captureStart: UtcInstant,
  captureEnd: UtcInstant,
  timestampKind: z.string(),
  rawSha256: Sha256Hex,
  redactedSha256: Sha256Hex,
  transform: z.string(),
  prevChainHash: Sha256Hex,
  chainHash: Sha256Hex,
  rawAvailable: z.boolean(),
  redactedAvailable: z.boolean(),
});
export type EvidenceSegment = z.infer<typeof EvidenceSegment>;

/**
 * Signed statement of what the round decided and on which evidence. Its canonical SHA-256 is signed with
 * the RESULT key and sent to the external witness.
 */
export const ResultRecord = z.object({
  recordVersion: z.literal(1),
  gameRoundId: GameRoundId,
  manifestHash: Sha256Hex,
  roundState: z.string(),
  finalCount: z.number().int().nonnegative().nullable(),
  winningSelections: z.array(z.string()),
  eventCount: z.number().int().nonnegative(),
  eventLogHash: Sha256Hex,
  evidence: z.object({
    status: z.enum(['COMPLETE', 'INSUFFICIENT', 'SIMULATION_ONLY']),
    sourceEpoch: z.string().nullable(),
    segmentCount: z.number().int().nonnegative(),
    firstSeq: z.number().int().nullable(),
    lastSeq: z.number().int().nullable(),
    chainHead: Sha256Hex.nullable(),
    problems: z.array(z.string()),
    alarms: z.array(z.string()),
  }),
  reason: z.string().nullable(),
  decidedAt: UtcInstant,
});
export type ResultRecord = z.infer<typeof ResultRecord>;

export const EvidenceProof = z.object({
  /** The locked manifest (its canonical hash is manifestHash). */
  manifest: RoundManifest,
  manifestSignature: Signature,
  manifestWitness: WitnessReceipt.nullable(),
  resultRecord: ResultRecord,
  resultHash: Sha256Hex,
  resultSignature: Signature,
  resultWitness: WitnessReceipt.nullable(),
  segments: z.array(EvidenceSegment),
  /** Where the verifier gets public keys (ours and the witness's). */
  keysUrl: z.string(),
  witnessIndependent: z.boolean(),
});
export type EvidenceProof = z.infer<typeof EvidenceProof>;

export const EvidenceBundle = z.object({
  gameRoundId: GameRoundId,
  manifestHash: Sha256Hex,
  mediaSegmentHashes: z.array(Sha256Hex),
  events: z.array(CrossingEvent),
  count: z.number().int().nonnegative(),
  winningSelections: z.array(z.string()),
  modelHash: Sha256Hex.nullable(),
  /** Media the events were counted on (recorded sources can be replayed at the event PTS). */
  mediaRef: z.object({ sourceId: z.string(), sourceEpoch: z.string(), window: z.object({ startMicros: z.number().int(), endMicros: z.number().int() }) }).nullable(),
  roundState: z.string(),
  configHash: Sha256Hex,
  status: EvidenceStatus,
  /** Evidence integrity checks are distinct from independent confirmation of the AI count. */
  evidenceVerified: z.boolean(),
  countIndependentlyConfirmed: z.boolean(),
  generatedAt: UtcInstant,
  /** Lightning server seed, revealed once the round is final: sha256(seed) must equal the manifest commitment. */
  lightningReveal: z.object({ serverSeedHex: z.string().regex(/^[0-9a-f]{64}$/), commitment: Sha256Hex }).nullable(),
  /** Manifest, result record, signatures, witness receipts and the segment hash chain (P12 backend). */
  proof: EvidenceProof.optional(),
});
export type EvidenceBundle = z.infer<typeof EvidenceBundle>;
